import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { SuggestionCardsBlockConfig } from '@front-platform/content-builder';
import { suggestionCardsBlockResolveConfig } from '@front-platform/content-builder';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Inject,
  input,
} from '@angular/core';

import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { DoodProductModel } from '@core/models/product.model';

import { ModalsService } from '@core/services/modals/modals.service';

import { ProductStoreDispatcher } from '@common/dispatchers/product.dispatcher';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { ProductService } from '@core/services/product/product.service';
import { staggeredListAnimation } from '@shared/animations/list.animation';
import { ProductPushV2AtomHoverColors } from '@shared/interfaces/product-hover-colors.interface';
import {
  KioskProductOverviewModalComponent,
  KioskProductOverviewModalConfig,
} from '@shared/modals/kiosk-product-overview-modal/kiosk-product-overview-modal.component';
import { ProductAddModalConfig } from '@shared/modals/product-add-modal/product-add-modal.component';
import { KioskGlobalSettingsService } from '@store/kiosk-global-settings/kiosk-global-settings.service';
import { Router } from '@angular/router';
import { suggestionProductOrigin } from '@core/models/suggestion-channel.util';
import { Store } from '@ngrx/store';
import { PRODUCT_STORE_COMPONENT_ACTIONS } from '@store/product/product.action';
import {
  SUGGESTION_CARDS_BLOCK_DATA_SOURCE,
  SUGGESTION_CARDS_BLOCK_SUGGESTIONS_SET,
  SuggestionCardsBlockDataSource,
} from './suggestion-cards-block-view-model';
import { provideSuggestionCardsBlockDataSource } from './suggestion-cards-block-data-source.providers';
import { productMatchesCatalogFilters } from '@shared/blocks/shop-product-list-block/catalog-filters';
import { ShopCatalogFiltersService } from '@shared/blocks/shop-product-list-block/shop-catalog-filters.service';
import { coalesceContentBuilderColor } from '@shared/utils/content-builder-color/content-builder-color';
export interface SuggestionCardsBlockRuntimeConfig {
  title_color?: IContentBuilderFieldColor;
}

export type SuggestionCardsBlockConfigInput = Partial<
  Omit<SuggestionCardsBlockConfig, keyof SuggestionCardsBlockRuntimeConfig> &
    SuggestionCardsBlockRuntimeConfig
>;
export type SuggestionCardsBlockResolvedConfig = Omit<
  SuggestionCardsBlockConfig,
  keyof SuggestionCardsBlockRuntimeConfig
> &
  Partial<SuggestionCardsBlockRuntimeConfig>;
export interface SuggestionCardsBlockContract extends ConfigComponentContract<
  SuggestionCardsBlockConfigInput,
  SuggestionCardsBlockResolvedConfig
> {}

@Component({
  selector: 'app-suggestion-cards-block',
  templateUrl: './suggestion-cards-block.component.html',
  animations: [staggeredListAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [...provideSuggestionCardsBlockDataSource()],
})
export class SuggestionCardsBlockComponent implements SuggestionCardsBlockContract {
  readonly config = input<SuggestionCardsBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    SuggestionCardsBlockConfig,
    SuggestionCardsBlockRuntimeConfig
  >(suggestionCardsBlockResolveConfig, () => ({ config: this.config() }));
  readonly title = computed(() => this.resolvedConfig()['title'] as unknown as string);
  readonly description = computed(() => this.resolvedConfig()['description'] as unknown as string);
  readonly suggestionsSet = computed(
    () => this.resolvedConfig()['suggestions_set'] as unknown as string,
  );
  readonly displayType = computed(() => this.resolvedConfig()['display_type'] as unknown as string);
  readonly backgroundColor = computed(
    () => this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly titleColor = computed(
    () => this.resolvedConfig()['title_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly textColor = computed(
    () => this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly cardColor = computed(
    () => this.resolvedConfig()['card_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly buttonBackgroundColor = computed(
    () => this.resolvedConfig()['button_background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly buttonTextColor = computed(
    () => this.resolvedConfig()['button_text_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly secondaryColor = computed(
    () => this.resolvedConfig()['secondary_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly priceColor = computed(
    () => this.resolvedConfig()['price_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly productNameColor = computed(
    () => this.resolvedConfig()['product_name_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly productDescriptionColor = computed(
    () =>
      this.resolvedConfig()['product_description_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly productPriceColor = computed(
    () => this.resolvedConfig()['product_price_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly productImagePlaceholderColor = computed(
    () =>
      this.resolvedConfig()[
        'product_image_placeholder_color'
      ] as unknown as IContentBuilderFieldColor,
  );
  readonly productBorderColor = computed(
    () => this.resolvedConfig()['product_border_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly productBackgroundColor = computed(
    () => this.resolvedConfig()['product_background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly productHoverColors = computed(
    () => this.resolvedConfig()['product_hover_colors'] as unknown as ProductPushV2AtomHoverColors,
  );
  readonly kioskFactoryPriceColor = computed(() =>
    coalesceContentBuilderColor(this.priceColor(), this.productPriceColor()),
  );
  readonly kioskFactoryCardColor = computed(() =>
    coalesceContentBuilderColor(this.cardColor(), this.productBackgroundColor()),
  );
  readonly kioskFactoryTextColor = computed(() =>
    coalesceContentBuilderColor(this.textColor(), this.productNameColor()),
  );
  readonly kioskFactoryBorderColor = computed(() => this.productBorderColor());
  readonly isSuggestionCardSticky = computed(
    () => this.resolvedConfig()['is_suggestion_card_sticky'] as unknown as boolean,
  );

  private readonly modalsService =
    inject<ModalsService<[KioskProductOverviewModalConfig, ProductAddModalConfig]>>(ModalsService);
  private readonly suggestionsSetRef = inject(SUGGESTION_CARDS_BLOCK_SUGGESTIONS_SET);
  private readonly productService = inject(ProductService);
  private readonly productDispatcher = inject(ProductStoreDispatcher);
  private readonly marketplaceStoreSelector = inject(MarketplaceStoreSelector);
  private readonly kioskGlobalSettings = inject(KioskGlobalSettingsService);
  private readonly catalogFilters = inject(ShopCatalogFiltersService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  protected readonly suggestionsVm$;

  protected visibleKioskSuggestions(products: DoodProductModel[]): DoodProductModel[] {
    const filters = this.catalogFilters.applied();
    if (!filters.length) {
      return products;
    }
    return products.filter(product => product && productMatchesCatalogFilters(product, filters));
  }

  protected readonly tagColors$ =
    this.marketplaceStoreSelector.selectMarketPlaceProductLabelsTagColors;

  protected readonly productCardColumns = computed(() => {
    return this.kioskGlobalSettings.productCardColSpan() === 2 ? 2 : 3;
  });

  constructor(
    @Inject(SUGGESTION_CARDS_BLOCK_DATA_SOURCE)
    blockDataSource: SuggestionCardsBlockDataSource,
  ) {
    this.suggestionsVm$ = blockDataSource.vm$;

    effect(() => {
      this.suggestionsSetRef.set(this.suggestionsSet());
    });
  }

  protected onNavigateTo(product: DoodProductModel): void {
    this.store.dispatch(
      PRODUCT_STORE_COMPONENT_ACTIONS.selectProduct({
        productId: product.id,
        productShopId: product.shop_id,
        productOrigin: this.suggestionOrigin(),
      }),
    );
  }

  protected addProductDispatcher(product: DoodProductModel): void {
    this.store.dispatch(
      PRODUCT_STORE_COMPONENT_ACTIONS.selectProduct({
        productId: product.id,
        productShopId: product.shop_id,
        productOrigin: this.suggestionOrigin(),
      }),
    );
  }

  private suggestionOrigin() {
    return suggestionProductOrigin(this.router.url, `suggestion_set_${this.suggestionsSet()}`);
  }

  protected openProductModal(product: DoodProductModel): void {
    this.productDispatcher.insertActive(product);
    this.modalsService.open(KioskProductOverviewModalComponent.handle, undefined, {
      image: product.images?.cover?.url,
      name: product.name,
      description: product.description,
      allergens: product.allergens,
      type: product.type,
      colors: {
        background: this.buttonTextColor(),
        accent: this.secondaryColor(),
      },
    });
  }

  protected isProductAvailable(product: DoodProductModel): boolean {
    return this.productService.isAvailable(product);
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(SuggestionCardsBlockComponent, { 'keyAliases': {} });
